import * as React from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Loader2, AlertCircle, Upload, Image as ImageIcon } from 'lucide-react';
import { api, Client, ApiError } from '@/lib/api';

export default function ClientEditPage() {
  const params = useParams();
  const navigate = useNavigate();
  const id = params.id as string;

  const [client, setClient] = React.useState<Client | null>(null);
  const [isLoading, setIsLoading] = React.useState(true);
  const [isSaving, setIsSaving] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [logoPreview, setLogoPreview] = React.useState<string | null>(null);
  const [logoFile, setLogoFile] = React.useState<File | null>(null);
  const fileInputRef = React.useRef<HTMLInputElement>(null);
  const [formData, setFormData] = React.useState({
    name: '',
    status: 'active' as 'active' | 'inactive' | 'suspended',
  });

  React.useEffect(() => {
    const loadClient = async () => {
      try {
        setIsLoading(true);
        setError(null);
        const data = await api.getClient(id);
        setClient(data);
        setFormData({
          name: data.name,
          status: data.status as 'active' | 'inactive' | 'suspended',
        });
        if (data.logoUrl) {
          setLogoPreview(data.logoUrl);
        }
      } catch (err) {
        const apiError = err as ApiError;
        setError(apiError.message || 'Erro ao carregar cliente');
      } finally {
        setIsLoading(false);
      }
    };

    loadClient();
  }, [id]);

  const handleLogoSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      alert('Tipo de arquivo inválido. Apenas imagens são permitidas (JPEG, PNG, GIF, WEBP)');
      return;
    }

    const maxSize = 5 * 1024 * 1024;
    if (file.size > maxSize) {
      alert('Arquivo muito grande. Tamanho máximo: 5MB');
      return;
    }

    const reader = new FileReader();
    reader.onloadend = () => {
      setLogoPreview(reader.result as string);
    };
    reader.readAsDataURL(file);
    setLogoFile(file);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSaving(true);
    setError(null);

    try {
      let logoUrl = client?.logoUrl;

      // Se uma nova logo foi selecionada, fazer upload
      if (logoFile) {
        try {
          const uploadFormData = new FormData();
          uploadFormData.append('logo', logoFile);

          // Fazer upload da logo do cliente
          const response = await fetch(`${import.meta.env.VITE_API_URL || 'http://localhost:3001/api'}/clients/${id}/logo`, {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${localStorage.getItem('accessToken')}`,
            },
            body: uploadFormData,
          });

          if (!response.ok) {
            throw new Error('Erro ao fazer upload da logo');
          }

          const uploadResult = await response.json();
          logoUrl = uploadResult.logoUrl;
        } catch (uploadError) {
          throw new Error('Erro ao fazer upload da imagem');
        }
      }

      // Atualizar cliente apenas se não houve upload de logo (logo já foi atualizado pelo endpoint de upload)
      if (!logoFile) {
        await api.updateClient(id, formData);
      }

      navigate(`/clients/${id}`);
    } catch (err) {
      const apiError = err as ApiError;
      setError(apiError.message || 'Erro ao atualizar cliente');
      setIsSaving(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="flex flex-col items-center gap-4">
          <Loader2 className="h-8 w-8 animate-spin text-[#0057FF]" />
          <p className="text-gray-500">Carregando cliente...</p>
        </div>
      </div>
    );
  }

  if (error && !client) {
    return (
      <div className="space-y-6">
        <div className="flex items-center gap-4">
          <Link
            to="/clients"
            className="p-2 rounded-xl text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors"
          >
            <ArrowLeft className="h-5 w-5" />
          </Link>
          <h1 className="text-2xl font-bold text-gray-900">Cliente nao encontrado</h1>
        </div>
        <div className="bg-red-50 rounded-2xl border border-red-100 p-6">
          <div className="flex items-center gap-4">
            <div className="h-12 w-12 rounded-xl bg-red-100 flex items-center justify-center">
              <AlertCircle className="h-6 w-6 text-red-500" />
            </div>
            <div>
              <h3 className="font-semibold text-red-800">Erro</h3>
              <p className="text-red-600 text-sm">{error}</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {/* Page Header */}
      <div className="flex items-center gap-4">
        <Link
          to={`/clients/${id}`}
          className="p-2 rounded-xl text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors"
        >
          <ArrowLeft className="h-5 w-5" />
        </Link>
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Editar Cliente</h1>
          <p className="text-gray-500 mt-1">
            Atualize as informacoes do cliente {client?.name}
          </p>
        </div>
      </div>

      {/* Error Alert */}
      {error && (
        <div className="bg-red-50 rounded-2xl border border-red-100 p-4">
          <div className="flex items-center gap-3">
            <AlertCircle className="h-5 w-5 text-red-500 shrink-0" />
            <p className="text-red-700 text-sm">{error}</p>
          </div>
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-8">
        {/* Basic Information */}
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
          <div className="p-6 border-b border-gray-100">
            <h2 className="text-lg font-semibold text-gray-900">Informacoes Basicas</h2>
            <p className="text-sm text-gray-500 mt-1">Dados principais do cliente</p>
          </div>
          <div className="p-6 space-y-6">
            <div className="grid gap-6 md:grid-cols-2">
              <div className="space-y-2">
                <label className="text-sm font-medium text-gray-700">Nome da Empresa *</label>
                <input
                  type="text"
                  required
                  value={formData.name}
                  onChange={(e) => setFormData((prev) => ({ ...prev, name: e.target.value }))}
                  placeholder="Ex: ACME Corporation"
                  className="w-full rounded-xl border border-gray-200 bg-white px-4 py-3 text-sm placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-[#0057FF]/20 focus:border-[#0057FF] transition-all"
                />
              </div>

              <div className="space-y-2">
                <label className="text-sm font-medium text-gray-700">Logo (Opcional)</label>
                <div className="flex items-center gap-4">
                  {logoPreview ? (
                    <div className="relative h-20 w-20 rounded-xl border-2 border-gray-200 overflow-hidden bg-white">
                      <img
                        src={logoPreview}
                        alt="Logo preview"
                        className="h-full w-full object-contain p-2"
                      />
                    </div>
                  ) : (
                    <div className="h-20 w-20 rounded-xl border-2 border-dashed border-gray-300 flex items-center justify-center bg-gray-50">
                      <ImageIcon className="h-8 w-8 text-gray-400" />
                    </div>
                  )}
                  <div className="flex-1">
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept="image/*"
                      onChange={handleLogoSelect}
                      className="hidden"
                    />
                    <button
                      type="button"
                      onClick={() => fileInputRef.current?.click()}
                      className="inline-flex items-center gap-2 px-4 py-2 rounded-xl border border-gray-200 text-sm font-medium text-gray-600 hover:bg-gray-50 transition-colors"
                    >
                      <Upload className="h-4 w-4" />
                      {logoPreview ? 'Alterar Logo' : 'Selecionar Logo'}
                    </button>
                    <p className="text-xs text-gray-400 mt-2">
                      JPEG, PNG, GIF ou WEBP (máx. 5MB)
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium text-gray-700">Status *</label>
              <select
                value={formData.status}
                onChange={(e) => setFormData((prev) => ({ ...prev, status: e.target.value as any }))}
                className="w-full rounded-xl border border-gray-200 bg-white px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#0057FF]/20 focus:border-[#0057FF] transition-all"
              >
                <option value="active">Ativo</option>
                <option value="inactive">Inativo</option>
                <option value="suspended">Suspenso</option>
              </select>
            </div>
          </div>
        </div>

        {/* Actions */}
        <div className="flex items-center justify-end gap-4">
          <Link
            to={`/clients/${id}`}
            className="px-6 py-3 rounded-xl border border-gray-200 text-sm font-medium text-gray-600 hover:bg-gray-50 transition-colors"
          >
            Cancelar
          </Link>
          <button
            type="submit"
            disabled={isSaving}
            className="inline-flex items-center gap-2 px-6 py-3 rounded-xl bg-gradient-to-r from-[#0057FF] to-[#7C3AED] text-white font-medium text-sm hover:opacity-90 transition-opacity shadow-lg shadow-[#0057FF]/25 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isSaving ? (
              <>
                <Loader2 className="h-5 w-5 animate-spin" />
                Salvando...
              </>
            ) : (
              'Salvar Alteracoes'
            )}
          </button>
        </div>
      </form>
    </div>
  );
}
